import React, { useCallback, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { uploadImage } from '~/services/ImageService';
import { addLocation } from '~/services/TripPlannerService';

const MAX_FILE_SIZE = 5_000_000;
const IMAGE_DROPZONE_ACCEPT = { 'image/*': [] };

interface LocationImageUploaderProps {
  destinationId: string;
  locationName: string;
}

function buildImageId(locationName: string, destinationId: string) {
  const normalize = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const slug = normalize(locationName) || normalize(destinationId) || 'unknown';

  return `${uuidv4()}-${slug}`;
}

export default function LocationImageUploader({ destinationId, locationName }: LocationImageUploaderProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const imageId = buildImageId(locationName, destinationId);
      await uploadImage(file, imageId);
      await addLocation({ destinationId, imageId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error';
      enqueueSnackbar(`Failed to upload image: ${message}`, { variant: 'error' });
    },
  });

  const handleDrop = useCallback(
    (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>) => {
      fileRejections.forEach((fileRejection) => {
        const { file, errors } = fileRejection;
        enqueueSnackbar(`"${file.name}" ${errors.map((err) => err.message).join(' and ')}`, { variant: 'error' });
      });

      if (!acceptedFiles.length || uploadMutation.isPending) {
        return;
      }

      const [file] = acceptedFiles;
      uploadMutation.mutate(file);
    },
    [enqueueSnackbar, uploadMutation],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: IMAGE_DROPZONE_ACCEPT,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: uploadMutation.isPending,
    noKeyboard: true,
    onDrop: handleDrop,
  });

  const label = useMemo(() => {
    if (uploadMutation.isPending) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2">Uploading...</Typography>
        </Stack>
      );
    }

    return <CloudUploadIcon fontSize="small" color={isDragActive ? 'primary' : 'action'} />;
  }, [isDragActive, uploadMutation.isPending]);

  return (
    <Box
      {...getRootProps({ 'aria-label': 'Upload image', title: 'Upload image' })}
      sx={{
        border: '1px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        minWidth: 72,
        minHeight: 48,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
        bgcolor: isDragActive ? 'action.hover' : 'background.default',
      }}
    >
      <input {...getInputProps()} />
      {label}
    </Box>
  );
}
